import type { AnyNode } from '@/types/nodes'
import type { Cable } from '@/types/fibers'
import { generarEstructuraFibras } from '@/utils/generarEstructuraFibras'

export type CableTipoUpper = 'TRONCAL' | 'DISTRIBUCION' | 'ACOMETIDA' | 'DROP'

const VALID_TIPOS: CableTipoUpper[] = ['TRONCAL', 'DISTRIBUCION', 'ACOMETIDA', 'DROP']

export function parseCableTipoUpper(s: string | undefined, fallback: CableTipoUpper): CableTipoUpper {
  const u = (s || '').toUpperCase().replace(/\s+/g, '_').normalize('NFD').replace(/\p{M}/gu, '')
  if (VALID_TIPOS.includes(u as CableTipoUpper)) return u as CableTipoUpper
  return fallback
}

type RutaPoint = { lat: number; lng: number; tipo: 'terminal' | 'poste'; nodeId?: string }

function buildRuta(origen: AnyNode, destino: AnyNode, postesIds: string[], allNodes: AnyNode[]): RutaPoint[] {
  const ruta: RutaPoint[] = []
  ruta.push({
    lat: origen.lat,
    lng: origen.lng,
    tipo: 'terminal',
    nodeId: origen.id,
  })
  for (const pid of postesIds) {
    const poste = allNodes.find((n) => n.id === pid)
    if (poste) {
      ruta.push({
        lat: poste.lat,
        lng: poste.lng,
        tipo: 'poste',
        nodeId: pid,
      })
    }
  }
  ruta.push({
    lat: destino.lat,
    lng: destino.lng,
    tipo: 'terminal',
    nodeId: destino.id,
  })
  return ruta
}

function segmentMeters(p1: { lat: number; lng: number }, p2: { lat: number; lng: number }): number {
  const R = 6371000
  const φ1 = (p1.lat * Math.PI) / 180
  const φ2 = (p2.lat * Math.PI) / 180
  const Δφ = ((p2.lat - p1.lat) * Math.PI) / 180
  const Δλ = ((p2.lng - p1.lng) * Math.PI) / 180
  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

export function computeLongitudTotalRuta(ruta: Array<{ lat: number; lng: number }>): number {
  let total = 0
  for (let i = 0; i < ruta.length - 1; i++) {
    total += segmentMeters(ruta[i], ruta[i + 1])
  }
  return total
}

export interface CreateCableBetweenNodesOptions {
  origenId: string
  destinoId: string
  nodes: AnyNode[]
  numeroFibras: number
  tipo?: string
  observaciones?: string
  postesIntermedios?: string[]
}

/**
 * Crea un cable (POST + actualización de fibras) igual que CableFormModal sin lógica DROP.
 */
export async function createCableBetweenNodes(
  options: CreateCableBetweenNodesOptions,
  addCable: (cable: Cable) => Promise<Cable>,
  updateCable: (cable: Cable) => Promise<void>
): Promise<Cable> {
  const origen = options.nodes.find((n) => n.id === options.origenId)
  const destino = options.nodes.find((n) => n.id === options.destinoId)
  if (!origen || !destino) {
    throw new Error('Nodos origen o destino no encontrados en el proyecto')
  }

  const tipo = parseCableTipoUpper(options.tipo, 'DISTRIBUCION')
  const numFibras = Math.max(1, Math.min(288, Math.round(options.numeroFibras)))
  const tieneBuffers = tipo !== 'DROP' && numFibras > 2

  const postesIds = options.postesIntermedios ?? []
  const ruta = buildRuta(origen, destino, postesIds, options.nodes)
  const longitudTotal = computeLongitudTotalRuta(ruta)

  const origenNombre = (origen as { nombre?: string }).nombre || origen.tipo
  const destinoNombre = (destino as { nombre?: string }).nombre || destino.tipo
  const nombreAuto = `${tipo}-${numFibras}FO ${origenNombre} a ${destinoNombre}`
  const timestamp = Date.now().toString(36).toUpperCase().slice(-4)
  const codigoAuto = `${tipo.slice(0, 3)}-${numFibras}-${timestamp}`

  const cableBasico = {
    nombre: nombreAuto,
    codigo: codigoAuto,
    tipo,
    numeroFibras: numFibras,
    ruta,
    postesIds,
    longitudTotal,
    longitudUsada: 0,
    tieneBuffers,
    buffers: null,
    fibras: null,
    estado: 'ACTIVO',
    origenId: options.origenId,
    destinoId: options.destinoId,
    observaciones: options.observaciones,
  } as unknown as Cable

  const createdCable = await addCable(cableBasico)
  const cableIdReal = (createdCable as { id?: string })?.id
  if (cableIdReal) {
    const { buffers, fibras } = generarEstructuraFibras(cableIdReal, numFibras, tieneBuffers)
    await updateCable({
      ...(createdCable as Cable),
      buffers,
      fibras,
    } as Cable)
  }

  return createdCable
}
